import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { request } from '../lib/api.js';

/**
 * Notification tray.
 *
 * The rule this is built around: **a notification is a request to act, not a feed.** If a
 * tray fills with things nobody does anything about, people stop opening it, and then the
 * one that mattered is missed too.
 *
 * So there are two kinds, kept visibly apart:
 *
 *   - **À faire** — someone must do something. Stays until it is done, not until it is seen.
 *     A customer waiting for a reply, an overdue invoice, a technician's rejected sync.
 *   - **Activité** — worth knowing, cleared by reading. A quote was accepted, a payment
 *     arrived.
 *
 * Repeats collapse: twelve messages in one conversation are one row with a count, because
 * twelve rows would bury everything else.
 */

type Notification = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  severity: 'info' | 'action';
  entity: string | null;
  entityId: string | null;
  actorName: string | null;
  count: number;
  readAt: string | null;
  createdAt: string;
};

export function NotificationTray() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => request<{ items: Notification[]; actionCount: number; unreadCount: number }>('/notifications'),
    // Live events push new rows in, so this is a safety net for a missed socket message
    // rather than the primary delivery path.
    refetchInterval: 5 * 60_000,
  });

  const markRead = useMutation({
    mutationFn: (ids: string[]) => request<void>('/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const resolve = useMutation({
    mutationFn: (id: string) => request<void>(`/notifications/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = notifications.data?.items ?? [];
  const actions = items.filter((n) => n.severity === 'action');
  const activity = items.filter((n) => n.severity === 'info');
  const actionCount = notifications.data?.actionCount ?? 0;
  const unreadCount = notifications.data?.unreadCount ?? 0;

  return (
    <div className="tray">
      <button
        type="button"
        className={`tray__toggle${actionCount > 0 ? ' tray__toggle--action' : ''}`}
        aria-expanded={open}
        aria-label={
          actionCount > 0
            ? `Notifications : ${actionCount} à faire`
            : unreadCount > 0
              ? `Notifications : ${unreadCount} non lues`
              : 'Notifications'
        }
        onClick={() => {
          setOpen((v) => !v);
          // Opening the tray clears the *unread* marker on activity, but never resolves an
          // action — seeing something is not doing it.
          const unread = activity.filter((n) => !n.readAt).map((n) => n.id);
          if (!open && unread.length) markRead.mutate(unread);
        }}
      >
        <BellGlyph />
        {/* The count shown is what needs doing. Unread-but-informational does not warrant a
            red badge; that is how badges become noise. */}
        {actionCount > 0 ? (
          <span className="tray__badge tray__badge--action tnum">{actionCount}</span>
        ) : unreadCount > 0 ? (
          <span className="tray__badge tnum">{unreadCount}</span>
        ) : null}
      </button>

      {open && (
        <div className="tray__panel" role="dialog" aria-label="Notifications">
          {items.length === 0 && (
            <p className="tray__empty">
              Rien à faire pour l’instant.
              <span>Les demandes des clients et les factures en retard apparaîtront ici.</span>
            </p>
          )}

          {actions.length > 0 && (
            <section className="tray__section">
              <header className="label tray__heading">À faire</header>
              {actions.map((n) => (
                <Row key={n.id} notification={n} onResolve={() => resolve.mutate(n.id)} onNavigate={() => setOpen(false)} />
              ))}
            </section>
          )}

          {activity.length > 0 && (
            <section className="tray__section">
              <header className="label tray__heading">Activité</header>
              {activity.map((n) => (
                <Row key={n.id} notification={n} onNavigate={() => setOpen(false)} />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  notification,
  onResolve,
  onNavigate,
}: {
  notification: Notification;
  onResolve?: () => void;
  onNavigate: () => void;
}) {
  const to = destination(notification);

  const content = (
    <>
      <span className={`tray__dot tray__dot--${notification.kind.split('.')[0]}`} aria-hidden="true" />
      <span className="tray__body">
        <span className="tray__title">
          {notification.title}
          {notification.count > 1 && <span className="tray__count tnum">×{notification.count}</span>}
        </span>
        {notification.body && <span className="tray__text">{notification.body}</span>}
        <span className="tray__when">
          {relativeTime(notification.createdAt)}
          {notification.actorName ? ` · ${notification.actorName}` : ''}
        </span>
      </span>
    </>
  );

  return (
    <div className={`tray__row${notification.readAt ? '' : ' tray__row--unread'}`}>
      {to ? (
        <Link to={to} className="tray__link" onClick={onNavigate}>
          {content}
        </Link>
      ) : (
        <span className="tray__link">{content}</span>
      )}
      {onResolve && (
        <button type="button" className="tray__done" onClick={onResolve} aria-label="Marquer comme réglé">
          Réglé
        </button>
      )}
    </div>
  );
}

/** Where acting on a notification takes you. Unknown kinds simply are not links. */
function destination(n: Notification): string | undefined {
  if (!n.entityId) return undefined;
  switch (n.entity) {
    case 'customer':
      return `/customers/${n.entityId}`;
    case 'job':
      return `/jobs/${n.entityId}`;
    case 'invoice':
      return `/invoices?q=${n.entityId}`;
    case 'quote':
      return `/quotes?q=${n.entityId}`;
    case 'thread':
      return `/inbox?thread=${n.entityId}`;
    default:
      return undefined;
  }
}

/**
 * Relative time in French.
 *
 * Written out rather than pulled from a library because the tray needs four cases and a
 * date-formatting dependency for that is not worth the bundle.
 */
function relativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} jours`;
  return new Date(iso).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' });
}

function BellGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M4 7a4 4 0 0 1 8 0c0 3 1 4 1 4H3s1-1 1-4z" />
      <path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
    </svg>
  );
}
